import React, { useState, useEffect } from 'react';
import dropdown from '../assets/dropdown.svg';
import '../styles/APIRequests.css';
import trendUp from '../assets/trend-up.svg';

interface APIData {
  time: number;
  requests: number;
  latency: number;
}

const APIRequests: React.FC = () => {
  const [currentLatency, setCurrentLatency] = useState(3000);
  const [percentageChange, setPercentageChange] = useState(3.4);
  const [isIncrease, setIsIncrease] = useState(true);
  const [progress, setProgress] = useState(75); // Percentage for circular progress
  const [activeIndex, setActiveIndex] = useState(0);

  // Sample data for realistic API metrics
  const [apiData] = useState<APIData[]>([
    { time: 1, requests: 2800, latency: 2800 },
    { time: 2, requests: 3200, latency: 3200 },
    { time: 3, requests: 2950, latency: 2950 },
    { time: 4, requests: 3400, latency: 3400 },
    { time: 5, requests: 3000, latency: 3000 },
    { time: 6, requests: 3600, latency: 3600 },
    { time: 7, requests: 3150, latency: 3150 },
    { time: 8, requests: 3800, latency: 3800 },
    { time: 9, requests: 3300, latency: 3300 },
    { time: 10, requests: 3500, latency: 3500 },
  ]);

  // Calculate percentage change
  const calculateChange = (currentIndex: number) => {
    if (currentIndex === 0) return { percentage: 3.4, isIncrease: true };
    
    const current = apiData[currentIndex].latency;
    const previous = apiData[currentIndex - 1].latency;
    const change = ((current - previous) / previous) * 100;
    const absChange = Math.abs(change);
    const isIncrease = change > 0;
    
    return { percentage: Math.round(absChange * 10) / 10, isIncrease };
  };

  // Calculate progress percentage (0-100) based on latency
  const calculateProgress = (latency: number) => {
    const minLatency = 2500;
    const maxLatency = 4000;
    return Math.min(100, Math.max(0, ((latency - minLatency) / (maxLatency - minLatency)) * 100));
  };

  // Auto-update values with progress tracking
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex(prev => {
        const nextIndex = (prev + 1) % apiData.length;
        
        setTimeout(() => {
          const newLatency = apiData[nextIndex].latency;
          const { percentage, isIncrease } = calculateChange(nextIndex);
          
          // Calculate progress based on the current step (0 to 100% over all data points)
          const stepProgress = ((nextIndex + 1) / apiData.length) * 100;
          
          setCurrentLatency(newLatency);
          setPercentageChange(percentage);
          setIsIncrease(isIncrease);
          setProgress(stepProgress);
        }, 200);
        
        return nextIndex;
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [apiData.length]);

  // Animated Circular Progress Component with step-by-step progress
  const CircularProgress = ({ progress }: { progress: number }) => {
    const radius = 45;
    const strokeWidth = 8;
    const normalizedRadius = radius - strokeWidth * 2;
    const circumference = normalizedRadius * 2 * Math.PI;
    const strokeDasharray = `${circumference} ${circumference}`;
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    return (
      <div className="circular-progress-container">
        <svg
          height={radius * 2}
          width={radius * 2}
          className="circular-progress-svg"
        >
          <defs>
            {/* Gradient definition */}
            <linearGradient id="circularGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#E879F9" />
              <stop offset="50%" stopColor="#A855F7" />
              <stop offset="100%" stopColor="#7C3AED" />
            </linearGradient>
          </defs>
          
          {/* Background circle */}
          <circle
            stroke="#f0f0f0"
            fill="transparent"
            strokeWidth={strokeWidth}
            r={normalizedRadius}
            cx={radius}
            cy={radius}
          />
          
          {/* Progressive fill circle */}
          <circle
            stroke="url(#circularGradient)"
            fill="transparent"
            strokeWidth={strokeWidth}
            strokeDasharray={strokeDasharray}
            strokeLinecap="round"
            r={normalizedRadius}
            cx={radius}
            cy={radius}
            style={{
              strokeDashoffset,
              transition: 'stroke-dashoffset 0.8s ease-in-out',
              transform: 'rotate(-90deg)',
              transformOrigin: '50% 50%'
            }}
          />
        </svg>
      </div>
    );
  };

  return (
    <div className="api-requests">
      <div className="api-requests-chart">
        <CircularProgress progress={progress} />
      </div>

      <div className="api-requests-text">
        <div className="api-requests-header">
          <h3 className="api-requests-title">API Requests hits</h3>
          <img src={dropdown} alt="Options" className="api-requests-dropdown" />
        </div>

        <div className="api-requests-body">
          <div className="api-requests-stats-group">
            <div className="api-requests-stats-label">Latency</div>
            <div className="api-requests-stats-value">
              {currentLatency} rq/sec
              <div className="api-requests-stats-change">
                {isIncrease ? (
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 16 16" 
                    fill="none" 
                    className="api-requests-stats-change-icon"
                  >
                    <path 
                      d="M8 3L12 7H4L8 3Z" 
                      fill="#EF4444"
                    />
                  </svg>
                ) : (
                  <img src={trendUp} alt="Trend down" className="api-requests-stats-change-icon" />
                )}
                <span 
                  className="api-requests-stats-change-value"
                  style={{ color: isIncrease ? '#EF4444' : '#12B76A' }}
                >
                  {percentageChange}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default APIRequests; 